/**
 * Embed types for Datadog context, used as a feature adoption marker:
 *
 * - 'standalone'          — loaded directly, not embedded
 * - 'iframe'              — legacy iframe embed (__epilot API / bundle.js)
 * - 'sdk-iframe'          — new Journey Embed SDK using .asIframe()
 * - 'web-component'       — raw <epilot-journey> element, manually added
 * - 'sdk-web-component'   — new Journey Embed SDK using .asWebComponent()
 */
export enum EMBED_TYPE {
  STANDALONE = 'standalone',
  IFRAME = 'iframe',
  SDK_IFRAME = 'sdk-iframe',
  WEB_COMPONENT = 'web-component',
  SDK_WEB_COMPONENT = 'sdk-web-component'
}

/** Module-level store for the resolved embed type. */
let _embedType: EMBED_TYPE | undefined

/** Store the embed type early (before DD is ready) so it can be sent later. */
export const setEmbedType = (type: EMBED_TYPE) => {
  _embedType = type
}

/**
 * Whether we own the `document` we render into. Defaults to `false` so a missing
 * opt-in costs us our own title, never the host page's.
 */
let _ownsDocument = false

/** Set to `true` by the `index` entry, the only one serving its own document. */
export const setOwnsDocument = (owns: boolean) => {
  _ownsDocument = owns
}

/** @see {@link setOwnsDocument} */
export const ownsDocument = (): boolean => _ownsDocument

/** Whether the journey IS the page: our own document, inside no frame. The SSO
 * login redirects the whole page to the IdP, so it needs exactly this shape. */
export const isStandaloneEmbed = (): boolean => ownsDocument() && !isInIframe()

/**
 * Detects whether the app is running inside an iframe.
 */
const isInIframe = (): boolean => {
  try {
    return window.self !== window.top
  } catch (_error) {
    // cross-origin iframe — access to window.top throws
    return true
  }
}

/**
 * Resolves the embed type from the entry point and SDK marker.
 *
 * @param entry - Which entry point is calling: 'index' (standalone/iframe) or 'embed' (web component)
 * @param embedSource - The `embedSource` value from app params (set to 'sdk' by the Journey Embed SDK)
 */
export const resolveEmbedType = (
  entry: 'index' | 'embed',
  embedSource?: 'sdk' | undefined
): EMBED_TYPE => {
  if (_embedType) return _embedType

  // The 'embed' entry runs inside the host page's own document, so its query
  // string belongs to the customer and never carries our params — there the SDK
  // marker only ever arrives as the `embed-source` attribute, surfaced here as
  // `embedSource`. Falling back to the URL would silently downgrade every SDK
  // web-component embed to 'web-component'.
  const source =
    entry === 'embed'
      ? embedSource
      : (embedSource ??
        new URLSearchParams(window.location.search).get('embedSource'))
  const isSDK = source === 'sdk'

  let embedType: EMBED_TYPE

  if (entry === 'embed') {
    embedType = isSDK ? EMBED_TYPE.SDK_WEB_COMPONENT : EMBED_TYPE.WEB_COMPONENT
  } else if (isInIframe()) {
    embedType = isSDK ? EMBED_TYPE.SDK_IFRAME : EMBED_TYPE.IFRAME
  } else {
    embedType = EMBED_TYPE.STANDALONE
  }

  setEmbedType(embedType)

  return embedType
}

/**
 * Resolves the URL of the page the Journey is embedded in, for Datadog context.
 *
 * Only iframe embeds can learn the host page from `document.referrer`: there the
 * app runs on our own domain and the referrer IS the embedding parent page.
 *
 * In every other case the app runs directly in the host page's document (web
 * component) or is the page itself (standalone), so `href` is already the host
 * page and `document.referrer` is the visitor's *traffic source* — a search
 * engine, a social network — which is not what this property means.
 *
 * Mirrors `getEmbedContextValues`, which applies the same rule to a submission's
 * `journey_context`.
 */
export const resolveHostPageUrl = ({
  embedType,
  href,
  referrer
}: {
  embedType: EMBED_TYPE
  href: string
  referrer: string
}): string => {
  const isIframe =
    embedType === EMBED_TYPE.IFRAME || embedType === EMBED_TYPE.SDK_IFRAME

  // An empty referrer means the host page suppressed it (e.g. a `no-referrer`
  // policy) — fall back to our own URL rather than reporting an empty string.
  if (isIframe) return referrer || href

  return href
}
